import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { DoorWall, MapLayoutDocument } from "@/lib/office/personal-office-layout";
import { isKnownMapId, MAP_REGISTRY } from "@/lib/office/personal-office-layout";
import {
  type EditorItemRef,
  type EditorSelectionSet,
  primarySelection,
  updateFurniture,
  updateOffice,
  updateTransition,
  updateZone,
} from "./map-layout-utils";

type Props = {
  layout: MapLayoutDocument;
  selection: EditorSelectionSet;
  onChange: (layout: MapLayoutDocument) => void;
  onDelete: () => void;
  onDuplicate: () => void;
};

const DOORS: DoorWall[] = ["N", "S", "E", "W"];

const MAP_OPTIONS = MAP_REGISTRY.map((m) => ({ id: m.id, label: m.label }));

function NumberField({
  id,
  label,
  value,
  min,
  max,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </div>
  );
}

function MultiSummary({
  selection,
  onDelete,
  onDuplicate,
}: {
  selection: EditorSelectionSet;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const counts = { office: 0, zone: 0, furniture: 0, spawn: 0, transition: 0 };
  for (const s of selection) {
    counts[s.kind] += 1;
  }
  const parts = Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${k}${n === 1 ? "" : "s"}`);

  return (
    <div className="space-y-3 p-3">
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground">Multi-select</p>
        <p className="text-sm font-medium">{selection.length} items</p>
        <p className="text-xs text-muted-foreground">{parts.join(" · ")}</p>
      </div>
      <p className="text-xs text-muted-foreground">
        Drag to move together · arrow keys nudge · Delete removes all · ⌘D duplicates
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onDuplicate}>
          Duplicate
        </Button>
        <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
          Delete all
        </Button>
      </div>
    </div>
  );
}

export function MapLayoutInspector({ layout, selection, onChange, onDelete, onDuplicate }: Props) {
  if (selection.length === 0) {
    return (
      <div className="space-y-2 p-3 text-sm text-muted-foreground">
        <p>Select an office, room, furniture piece, transition, or spawn on the map.</p>
        <p className="text-xs">
          <strong className="text-foreground">Tips:</strong> Shift/⌘-click to multi-select · drag
          empty canvas for marquee · SE corner handle resizes · ⌘Z undo
        </p>
      </div>
    );
  }

  if (selection.length > 1) {
    return <MultiSummary selection={selection} onDelete={onDelete} onDuplicate={onDuplicate} />;
  }

  const sel: EditorItemRef | null = primarySelection(selection);
  if (!sel) return null;

  if (sel.kind === "office") {
    const o = layout.offices.find((x) => x.slotKey === sel.id);
    if (!o) return null;
    return (
      <div className="space-y-3 p-3">
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground">Office</p>
          <p className="font-mono text-xs text-muted-foreground">{o.slotKey}</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="office-name">Name</Label>
          <Input
            id="office-name"
            value={o.nameDefault}
            onChange={(e) =>
              onChange(updateOffice(layout, o.slotKey, { nameDefault: e.target.value }))
            }
          />
        </div>
        <div className="space-y-1">
          <Label>Door wall</Label>
          <div className="flex gap-1">
            {DOORS.map((d) => (
              <Button
                key={d}
                type="button"
                size="sm"
                variant={o.doorWall === d ? "default" : "outline"}
                onClick={() => onChange(updateOffice(layout, o.slotKey, { doorWall: d }))}
              >
                {d}
              </Button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            id="o-x1"
            label="X1"
            value={o.x1}
            onChange={(n) => onChange(updateOffice(layout, o.slotKey, { x1: n }))}
          />
          <NumberField
            id="o-y1"
            label="Y1"
            value={o.y1}
            onChange={(n) => onChange(updateOffice(layout, o.slotKey, { y1: n }))}
          />
          <NumberField
            id="o-x2"
            label="X2"
            value={o.x2}
            onChange={(n) => onChange(updateOffice(layout, o.slotKey, { x2: n }))}
          />
          <NumberField
            id="o-y2"
            label="Y2"
            value={o.y2}
            onChange={(n) => onChange(updateOffice(layout, o.slotKey, { y2: n }))}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Size {o.x2 - o.x1 + 1}×{o.y2 - o.y1 + 1} · min 5×5
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
            Delete office
          </Button>
        </div>
      </div>
    );
  }

  if (sel.kind === "zone") {
    const z = layout.commonZones.find((x) => x.id === sel.id);
    if (!z) return null;
    return (
      <div className="space-y-3 p-3">
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground">Room / zone</p>
          <p className="font-mono text-xs text-muted-foreground">{z.id}</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="zone-name">Name</Label>
          <Input
            id="zone-name"
            value={z.name}
            onChange={(e) => onChange(updateZone(layout, z.id, { name: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            id="z-x1"
            label="X1"
            value={z.x1}
            onChange={(n) => onChange(updateZone(layout, z.id, { x1: n }))}
          />
          <NumberField
            id="z-y1"
            label="Y1"
            value={z.y1}
            onChange={(n) => onChange(updateZone(layout, z.id, { y1: n }))}
          />
          <NumberField
            id="z-x2"
            label="X2"
            value={z.x2}
            onChange={(n) => onChange(updateZone(layout, z.id, { x2: n }))}
          />
          <NumberField
            id="z-y2"
            label="Y2"
            value={z.y2}
            onChange={(n) => onChange(updateZone(layout, z.id, { y2: n }))}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="zone-kind">Room kind</Label>
          <select
            id="zone-kind"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={z.roomKind ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "meeting" || v === "huddle") {
                onChange(updateZone(layout, z.id, { roomKind: v, priv: true }));
              } else {
                onChange(updateZone(layout, z.id, { roomKind: undefined }));
              }
            }}
          >
            <option value="">Open / outdoor</option>
            <option value="meeting">Meeting</option>
            <option value="huddle">Huddle</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={z.priv === true}
            onChange={(e) => onChange(updateZone(layout, z.id, { priv: e.target.checked }))}
          />
          Private room
        </label>
        <p className="text-xs text-muted-foreground">
          {z.roomKind ?? (z.priv ? "private" : "open")} · rug {z.rug}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
            Delete room
          </Button>
        </div>
      </div>
    );
  }

  if (sel.kind === "furniture") {
    const f = layout.furniture.find((x) => x.id === sel.id);
    if (!f) return null;
    const isBuilding = f.kind === "building" || f.kind === "door";
    return (
      <div className="space-y-3 p-3">
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground">
            {isBuilding ? "Building piece" : "Furniture"}
          </p>
          <p className="capitalize">{f.kind}</p>
          <p className="font-mono text-[10px] text-muted-foreground">{f.id}</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <NumberField
            id="f-gx"
            label="Grid X"
            value={f.gx}
            min={0}
            onChange={(n) => onChange(updateFurniture(layout, f.id, { gx: n }))}
          />
          <NumberField
            id="f-gy"
            label="Grid Y"
            value={f.gy}
            min={0}
            onChange={(n) => onChange(updateFurniture(layout, f.id, { gy: n }))}
          />
          <NumberField
            id="f-w"
            label="Width (tiles)"
            value={f.w}
            min={1}
            max={16}
            onChange={(n) => onChange(updateFurniture(layout, f.id, { w: n }))}
          />
          <NumberField
            id="f-d"
            label="Depth (tiles)"
            value={f.d}
            min={1}
            max={16}
            onChange={(n) => onChange(updateFurniture(layout, f.id, { d: n }))}
          />
        </div>

        <div className="space-y-1">
          <Label>Facing / dir</Label>
          <div className="flex gap-1">
            {DOORS.map((d) => (
              <Button
                key={d}
                type="button"
                size="sm"
                variant={f.dir === d ? "default" : "outline"}
                onClick={() => onChange(updateFurniture(layout, f.id, { dir: d }))}
              >
                {d}
              </Button>
            ))}
          </div>
        </div>

        {isBuilding ? (
          <>
            <div className="space-y-1">
              <Label>Door wall (facade)</Label>
              <div className="flex gap-1">
                {DOORS.map((d) => (
                  <Button
                    key={d}
                    type="button"
                    size="sm"
                    variant={(f.doorWall ?? "S") === d ? "default" : "outline"}
                    onClick={() => onChange(updateFurniture(layout, f.id, { doorWall: d }))}
                  >
                    {d}
                  </Button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">
                S = front faces park when building is at top of map
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="f-name">Display name</Label>
              <Input
                id="f-name"
                value={f.displayName ?? ""}
                onChange={(e) =>
                  onChange(updateFurniture(layout, f.id, { displayName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="f-style">Facade style</Label>
              <Input
                id="f-style"
                value={f.style ?? ""}
                placeholder="cafe, delivery, …"
                onChange={(e) =>
                  onChange(
                    updateFurniture(layout, f.id, {
                      style: e.target.value.trim().toLowerCase() || undefined,
                    }),
                  )
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="f-color">Tint color</Label>
              <Input
                id="f-color"
                value={f.c ?? ""}
                placeholder="#4A7AB8"
                onChange={(e) =>
                  onChange(updateFurniture(layout, f.id, { c: e.target.value || undefined }))
                }
              />
            </div>
          </>
        ) : null}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={f.block === true}
            onChange={(e) => onChange(updateFurniture(layout, f.id, { block: e.target.checked }))}
          />
          Blocks walking
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={f.seat === true}
            onChange={(e) => onChange(updateFurniture(layout, f.id, { seat: e.target.checked }))}
          />
          Seat
        </label>

        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
            Delete
          </Button>
        </div>
      </div>
    );
  }

  if (sel.kind === "transition") {
    const t = (layout.transitions ?? []).find((x) => x.id === sel.id);
    if (!t) return null;
    return (
      <div className="space-y-3 p-3">
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground">Map transition</p>
          <p className="font-mono text-[10px] text-muted-foreground">{t.id}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            id="t-gx"
            label="Grid X"
            value={t.gx}
            onChange={(n) => onChange(updateTransition(layout, t.id, { gx: n }))}
          />
          <NumberField
            id="t-gy"
            label="Grid Y"
            value={t.gy}
            onChange={(n) => onChange(updateTransition(layout, t.id, { gy: n }))}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="t-label">Label</Label>
          <Input
            id="t-label"
            value={t.label ?? ""}
            onChange={(e) =>
              onChange(updateTransition(layout, t.id, { label: e.target.value || undefined }))
            }
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="t-map">Destination map</Label>
          <select
            id="t-map"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={t.toMapId}
            onChange={(e) => onChange(updateTransition(layout, t.id, { toMapId: e.target.value }))}
          >
            {MAP_OPTIONS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
            {!isKnownMapId(t.toMapId) ? <option value={t.toMapId}>{t.toMapId}</option> : null}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            id="t-sx"
            label="Spawn X"
            value={t.toSpawn.gx}
            onChange={(n) =>
              onChange(updateTransition(layout, t.id, { toSpawn: { ...t.toSpawn, gx: n } }))
            }
          />
          <NumberField
            id="t-sy"
            label="Spawn Y"
            value={t.toSpawn.gy}
            onChange={(n) =>
              onChange(updateTransition(layout, t.id, { toSpawn: { ...t.toSpawn, gy: n } }))
            }
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
            Delete transition
          </Button>
        </div>
      </div>
    );
  }

  const spawn = layout.spawnPoints[sel.index];
  if (!spawn) return null;
  return (
    <div className="space-y-3 p-3">
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground">Spawn</p>
        <p className="text-sm">
          {spawn.gx}, {spawn.gy}
        </p>
        <p className="text-xs text-muted-foreground">Drag on the map to move</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onDuplicate}>
          Duplicate
        </Button>
        <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
          Delete spawn
        </Button>
      </div>
    </div>
  );
}
